/**
 * App —— 認證閘 ＋ 路由（`ui-spec.md` 嘅頁面會陸續加）
 * ⚠️ 未登入／未載入主檔 → 一律顯示登入頁（唔准偷睇任何帳目）
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { api, type Me, type Meta } from './lib/api';
import Login from './pages/Login';
import ChangePin from './pages/ChangePin';
import Home from './pages/Home';
import Capture from './pages/Capture';
import Income from './pages/Income';
import Vouchers from './pages/Vouchers';
import VoucherDetail from './pages/VoucherDetail';
import Reports from './pages/Reports';
import Export from './pages/Export';
import Periods from './pages/Periods';
import Profit from './pages/Profit';
import Budget from './pages/Budget';
import OpenItems from './pages/OpenItems';
import Bank from './pages/Bank';
import Assets from './pages/Assets';
import More from './pages/More';
import Settings from './pages/Settings';
import Evidence from './pages/Evidence';
import AppNav from './components/AppNav';

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.me();
      setMe(r.user);
      setMeta(await api.meta());
    } catch {
      setMe(null);
      setMeta(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const logout = async () => {
    await api.logout();
    setMe(null);
    setMeta(null);
    navigate('/');
  };

  if (!ready) return <main className="page"><p>…</p></main>;
  if (!me || !meta) return <Login onLoggedIn={() => { void load(); navigate('/'); }} />;
  if (me.mustChangePin) return <ChangePin onDone={() => { void load(); }} onLogout={() => void logout()} />;

  return (
    <>
      <Routes>
        <Route path="/" element={<Home meta={meta} me={me} onLogout={() => void logout()} />} />
        <Route path="/capture" element={<Capture meta={meta} onDone={() => navigate('/')} />} />
        <Route path="/income" element={<Income meta={meta} onDone={() => navigate('/')} />} />
        <Route path="/vouchers" element={<Vouchers meta={meta} />} />
        <Route path="/vouchers/:id" element={<VoucherDetail me={me} />} />
        <Route path="/periods" element={<Periods meta={meta} me={me} />} />
        <Route path="/profit" element={<Profit meta={meta} />} />
        <Route path="/budget" element={<Budget meta={meta} />} />
        <Route path="/invoices" element={<OpenItems meta={meta} />} />
        <Route path="/bank" element={<Bank meta={meta} />} />
        <Route path="/assets" element={<Assets meta={meta} />} />
        <Route path="/reports" element={<Reports meta={meta} />} />
        <Route path="/export" element={<Export meta={meta} />} />
        <Route path="/more" element={<More onLogout={() => void logout()} />} />
        <Route path="/settings" element={<Settings onLogout={() => void logout()} />} />
        <Route path="/evidence" element={<Evidence meta={meta} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AppNav />
    </>
  );
}
